// NewTemplatePage.jsx
import { Alert, Box, Button, Container, IconButton, Snackbar, Typography } from "@mui/material";
import { ArrowForward, AutoAwesome as SparkleIcon } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import WorkoutDaysSection from "../componenets/new_templates/WorkoutDaysSection";
import { getTemplate, updateTemplate } from "../services/TemplatesService";
import {
    cleanTemplateExerciseForSaving,
    createSuperSetLink,
    createTemplateExerciseFromDefinition,
    duplicateWorkoutDay,
    getNextExerciseIndex,
    reorderExerciseGroups
} from "../utils/exerciseUtils";
import CollapsibleTemplateOverview from "../componenets/new_templates/CollapsibleTemplateOverview";
import { useAuth } from "../contexts/AuthContext";
import { enrichTemplateExercises } from "../utils/templateEnrichment";
import WorkoutAiDrawer from "../componenets/new_templates/ai/WorkoutAiDrawer";
import { RTL } from "../cache/CacheRtl";


const NewTemplatePage = () => {
    const { templateId } = useParams();
    const navigate = useNavigate();

    const [template, setTemplate] = useState(null);
    const [originalTemplate, setOriginalTemplate] = useState(null);
    const [numberOfDays, setNumberOfDays] = useState(0);
    const [templateLoading, setTemplateLoading] = useState(!!templateId);
    const { isSuperAdmin, isCompanyGroupAdmin } = useAuth();

    // A program is read-only to a viewer who cannot edit it: system templates to non-super-admins, and a
    // COMPANY_GROUP_ADMIN-authored program (ownerUserId set) to anyone who is not that owning group admin
    // (a group admin only ever loads its OWN programs, so being a group admin here implies ownership).
    const computeReadOnly = useCallback((t) => {
        if (!t) return false;
        if (t.systemResource) return !isSuperAdmin();
        if (t.ownerUserId) return !isCompanyGroupAdmin();
        return false;
    }, [isSuperAdmin, isCompanyGroupAdmin]);
    const isReadOnly = computeReadOnly(template);
    const readOnlyMessage = template?.systemResource
        ? 'לא ניתן לשנות תבנית מערכת'
        : `לא ניתן לשנות תוכנית שנוצרה על ידי ${template?.ownerName || 'מנהל קבוצת חברות'}`;
    const [saving, setSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [error, setError] = useState(null);
    const [aiDrawerOpen, setAiDrawerOpen] = useState(false);

    // Helper to convert enriched template back to clean template format for saving
    const cleanTemplateForSaving = (enrichedTemplate) => {
        if (!enrichedTemplate.workouts) return enrichedTemplate;
        return {
            ...enrichedTemplate,
            workouts: enrichedTemplate.workouts.map(workout => ({
                ...workout,
                exercises: workout.exercises?.map((exercise, index) =>
                    cleanTemplateExerciseForSaving(exercise, index)
                ) || []
            }))
        };
    };

    // Track whether the template has been modified since last save/load
    const templateVersionRef = useRef(0);
    const savedVersionRef = useRef(0);

    useEffect(() => {
        if (template && originalTemplate) {
            setHasUnsavedChanges(templateVersionRef.current !== savedVersionRef.current);
        }
    }, [template, originalTemplate]);

    // Fetch template data
    useEffect(() => {
        const fetchTemplate = async () => {
            if (!templateId) {
                setTemplateLoading(false);
                return;
            }
            try {
                setTemplateLoading(true);
                const response = await getTemplate(templateId);
                // Names and images live in the exercise library, not on the program. Shared with the
                // studio board, which cannot render a card without them.
                const templateData = await enrichTemplateExercises(response.data);

                setTemplate(templateData);
                setOriginalTemplate(JSON.parse(JSON.stringify(templateData)));
                setNumberOfDays(templateData.workouts?.length || 0);
            } catch (err) {
                console.error('Error fetching template:', err);
                setError('שגיאה בטעינת התבנית');
            } finally {
                setTemplateLoading(false);
            }
        };
        fetchTemplate();
    }, [templateId]);

    const setTemplateWithDirty = useCallback((updaterOrValue) => {
        templateVersionRef.current += 1;
        setTemplate(updaterOrValue);
    }, []);

    const handleTemplateUpdate = useCallback((updatedTemplateData) => {
        setTemplateWithDirty(prevTemplate => ({
            ...updatedTemplateData,
            workouts: prevTemplate.workouts
        }));
    }, [setTemplateWithDirty]);

    // Save template
    const handleSaveTemplate = useCallback(async () => {
        try {
            setSaving(true);
            setSaveError(null);
            const templateToSave = cleanTemplateForSaving(template);
            await updateTemplate(templateToSave);
            savedVersionRef.current = templateVersionRef.current;
            setHasUnsavedChanges(false);
            setSaveSuccess(true);
        } catch (err) {
            console.error('Error saving template:', err);
            setSaveError('שגיאה בשמירת התבנית');
        } finally {
            setSaving(false);
        }
    }, [template]);

    // Assign the program to a COMPANY_GROUP_ADMIN's authorized companies (their equivalent of "assign to
    // trainees"). It persists immediately through the same clean+PUT path as Save — reusing
    // cleanTemplateForSaving so the enriched workout fields are normalised (a raw PUT would 400 on unknown
    // props) — and mirrors the new companyPermissions into state WITHOUT dirtying, since it's already saved.
    // Throws on failure so the dialog can surface the error.
    const handleAssignCompanies = useCallback(async (companyPermissions) => {
        if (!template?.id) return;
        const payload = cleanTemplateForSaving({ ...template, companyPermissions });
        await updateTemplate(payload);
        setTemplate(prev => (prev ? { ...prev, companyPermissions } : prev));
    }, [template]);

    // Remove exercise from day
    const removeExerciseFromDay = useCallback((dayNumber, exerciseId) => {
        templateVersionRef.current += 1;
        setTemplate(prev => {
            if (!prev) return prev;
            const workoutIndex = prev.workouts?.findIndex(workout => workout.index === dayNumber);
            if (workoutIndex === -1) return prev;

            const updatedTemplate = { ...prev };
            updatedTemplate.workouts = [...prev.workouts];
            updatedTemplate.workouts[workoutIndex] = { ...prev.workouts[workoutIndex] };
            updatedTemplate.workouts[workoutIndex].exercises =
                (prev.workouts[workoutIndex].exercises?.filter(ex => {
                    const idToCheck = ex.exerciseId || ex.id;
                    return idToCheck !== exerciseId;
                }) || []).map((exercise, index) => ({ ...exercise, index }));
            return updatedTemplate;
        });
    }, []);

    // Reorder exercises within a day
    const handleReorderExercises = useCallback((dayNumber, fromIndex, toIndex) => {
        if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
        templateVersionRef.current += 1;
        setTemplate(prev => {
            if (!prev) return prev;
            const workoutIndex = prev.workouts?.findIndex(workout => workout.index === dayNumber);
            if (workoutIndex === -1) return prev;

            const exercises = prev.workouts[workoutIndex].exercises || [];
            if (fromIndex >= exercises.length || toIndex >= exercises.length) return prev;

            // Move whole GROUPS (a superset is a single 2-exercise unit) so dragging a superset
            // relocates both partners instead of orphaning the second. See reorderExerciseGroups.
            const newExercises = reorderExerciseGroups(exercises, fromIndex, toIndex);
            if (newExercises === exercises) return prev; // not a group boundary / no-op

            const updatedTemplate = { ...prev };
            updatedTemplate.workouts = [...prev.workouts];
            updatedTemplate.workouts[workoutIndex] = { ...prev.workouts[workoutIndex], exercises: newExercises };
            return updatedTemplate;
        });
    }, []);

    // Exercise update
    const handleExerciseUpdate = useCallback((dayNumber, updatedExercise) => {
        setTemplateWithDirty(prev => {
            if (!prev) return prev;
            const workoutIndex = prev.workouts?.findIndex(workout => workout.index === dayNumber);
            if (workoutIndex === -1) return prev;

            const exerciseIndex = prev.workouts[workoutIndex].exercises?.findIndex(
                ex => (ex.exerciseId || ex.id) === (updatedExercise.exerciseId || updatedExercise.id)
            );
            if (exerciseIndex === -1) return prev;

            const updatedTemplate = { ...prev };
            updatedTemplate.workouts = [...prev.workouts];
            updatedTemplate.workouts[workoutIndex] = { ...prev.workouts[workoutIndex] };
            updatedTemplate.workouts[workoutIndex].exercises = [...prev.workouts[workoutIndex].exercises];
            updatedTemplate.workouts[workoutIndex].exercises[exerciseIndex] = updatedExercise;
            return updatedTemplate;
        });
    }, [setTemplateWithDirty]);

    // Add exercises to a day (from dialog)
    const handleAddExercises = useCallback((dayNumber, exerciseDefinitions) => {
        if (!template) return;
        if (computeReadOnly(template)) {
            setError(readOnlyMessage);
            return;
        }

        templateVersionRef.current += 1;
        setTemplate(prev => {
            if (!prev) return prev;
            const workoutIndex = prev.workouts?.findIndex(workout => workout.index === dayNumber);
            if (workoutIndex === -1) return prev;

            const workout = prev.workouts[workoutIndex];
            const existingExercises = workout.exercises || [];
            let nextIndex = getNextExerciseIndex(existingExercises);

            const newExercises = exerciseDefinitions
                .filter(def => !existingExercises.some(ex => ex.exerciseId === def.id))
                .map(def => createTemplateExerciseFromDefinition(def, nextIndex++));

            if (newExercises.length === 0) return prev;

            const updatedTemplate = { ...prev };
            updatedTemplate.workouts = [...prev.workouts];
            updatedTemplate.workouts[workoutIndex] = {
                ...prev.workouts[workoutIndex],
                exercises: [...existingExercises, ...newExercises]
            };
            return updatedTemplate;
        });
    }, [template, computeReadOnly, readOnlyMessage]);

    // Add super set to a day
    const handleAddSuperSet = useCallback((dayNumber, exerciseDefA, exerciseDefB) => {
        if (!template) return;
        if (computeReadOnly(template)) {
            setError(readOnlyMessage);
            return;
        }

        templateVersionRef.current += 1;
        setTemplate(prev => {
            if (!prev) return prev;
            const workoutIndex = prev.workouts?.findIndex(workout => workout.index === dayNumber);
            if (workoutIndex === -1) return prev;

            const workout = prev.workouts[workoutIndex];
            const existingExercises = workout.exercises || [];
            let nextIndex = getNextExerciseIndex(existingExercises);

            const templateExA = createTemplateExerciseFromDefinition(exerciseDefA, nextIndex);
            const templateExB = createTemplateExerciseFromDefinition(exerciseDefB, nextIndex + 1);

            // Link as superset
            const { exerciseA: linkedA, exerciseB: linkedB } = createSuperSetLink(templateExA, templateExB);

            const updatedTemplate = { ...prev };
            updatedTemplate.workouts = [...prev.workouts];
            updatedTemplate.workouts[workoutIndex] = {
                ...prev.workouts[workoutIndex],
                exercises: [...existingExercises, linkedA, linkedB]
            };
            return updatedTemplate;
        });
    }, [template, computeReadOnly, readOnlyMessage]);

    // ========== AI Assistant: convert preview workouts → enriched template state ==========
    const aiPreviewExerciseToTemplate = (ex, index) => {
        const exDef = ex.exercise || ex; // marker payloads attach the resolved exercise on `.exercise`,
                                         // but seeded exercises already have those fields at top level
        const base = {
            id: null,
            templateExerciseId: null,
            createdDate: null,
            index,
            exerciseId: ex.exerciseId,
            exerciseName: ex.exerciseName || exDef.exerciseName || exDef.nameHeb || exDef.name,
            imageUrl: ex.imageUrl || exDef.imageUrl || null,
            coverUrl: ex.coverUrl || exDef.coverUrl || null,
            targetMuscle: ex.targetMuscle || exDef.targetMuscle || 'UNKNOWN',
            secondaryMuscle: ex.secondaryMuscle || exDef.secondaryMuscle || 'UNKNOWN',
            comment: null,
            superSetGroupId: null,
            typeSet: 'REGULAR_SET',
        };

        if (ex.exerciseType === 'CARDIO') {
            return {
                ...base,
                tempo: null,
                manipulation: null,
                catalyst: false,
                durationTime: ex.durationSec || 60,
                restTimeInSec: 0,
                recommendedReps: 0,
                numberOfSets: 0,
            };
        }
        if (ex.exerciseType === 'CATALYST') {
            return {
                ...base,
                tempo: null,
                manipulation: 'RP_NEW',
                catalyst: true,
                durationTime: null,
                restTimeInSec: ex.restSec || 120,
                recommendedReps: 0,
                numberOfSets: ex.sets || 3,
                defaultMinDuration: ex.minDuration || 30,
                defaultMaxDuration: ex.maxDuration || 60,
                recommendedDuration: Math.round(((ex.minDuration || 30) + (ex.maxDuration || 60)) / 2),
            };
        }
        // strength (default)
        return {
            ...base,
            tempo: ex.tempo || 'T1_0_1_0',
            manipulation: 'RP_NEW',
            catalyst: false,
            durationTime: null,
            restTimeInSec: ex.restSec || 120,
            recommendedReps: ex.reps || 10,
            numberOfSets: ex.sets || 3,
        };
    };

    const handleAiApply = useCallback(async (preview, mode) => {
        if (!template || !preview?.workouts) return;
        templateVersionRef.current += 1;

        // Convert preview → enriched template workout/exercise shape
        const previewWorkouts = preview.workouts.map((w, wIdx) => ({
            id: null,
            index: wIdx,
            name: w.name || `Workout ${wIdx + 1}`,
            nameHeb: w.nameHeb || w.name || `אימון ${wIdx + 1}`,
            exercises: (w.exercises || []).map((ex, eIdx) => aiPreviewExerciseToTemplate(ex, eIdx)),
        }));

        let newWorkouts;
        if (mode === 'merge') {
            const existing = template.workouts || [];
            // Append AI workouts after existing ones, renumber
            const merged = [...existing, ...previewWorkouts];
            newWorkouts = merged.map((w, i) => ({ ...w, index: i }));
        } else {
            newWorkouts = previewWorkouts;
        }

        const updatedTemplate = { ...template, workouts: newWorkouts };
        setTemplate(updatedTemplate);
        setNumberOfDays(newWorkouts.length);

        // Save immediately so the user doesn't lose work if they close the page
        try {
            setSaving(true);
            setSaveError(null);
            const templateToSave = cleanTemplateForSaving(updatedTemplate);
            await updateTemplate(templateToSave);
            savedVersionRef.current = templateVersionRef.current;
            setHasUnsavedChanges(false);
            setSaveSuccess(true);
        } catch (err) {
            console.error('Error saving AI-applied template:', err);
            setSaveError('שגיאה בשמירת התבנית');
        } finally {
            setSaving(false);
        }
    }, [template]);

    // Build the program-context block sent to the AI as system-prompt context
    const aiProgramContext = useMemo(() => {
        if (!template) return null;
        return {
            gender: template.gender || null,
            experienceLevel: template.experienceLevel || null,
            bodyPart: template.bodyPart || null,
            secondaryMuscle: template.secondaryMuscle || null,
            equipmentType: template.equipmentType || null,
            trainDuration: template.trainDuration || null,
            goals: template.goals || null,
        };
    }, [template]);

    // Add new day
    const addNewDay = useCallback(() => {
        if (numberOfDays < 14 && template) {
            const newDayNumber = numberOfDays;
            const newWorkout = {
                id: null,
                index: newDayNumber,
                name: `Workout ${newDayNumber + 1}`,
                nameHeb: `אימון ${newDayNumber + 1}`,
                exercises: []
            };
            setTemplateWithDirty(prev => ({
                ...prev,
                workouts: [...(prev.workouts || []), newWorkout]
            }));
            setNumberOfDays(prev => prev + 1);
        }
    }, [numberOfDays, template, setTemplateWithDirty]);

    // Duplicate day
    // Put ONE workout on the studio screen, in a NEW TAB: the board is meant to be dragged onto a
    // second display and left up, while this tab stays where the trainer was working. It is a
    // read-only view of what is SAVED — the board re-fetches the program — so unsaved edits would
    // simply not be on it, which is worth saying out loud rather than letting them wonder.
    const handlePresentDay = useCallback((dayNumber) => {
        if (hasUnsavedChanges && !window.confirm('יש שינויים שלא נשמרו — הם לא יופיעו בתצוגת המסך. להמשיך?')) {
            return;
        }
        // No `noopener`: the board tab closes ITSELF when the trainer is done with it, and a
        // browser only lets a tab do that when it knows a script opened it. Same origin, our own
        // app — there is nothing to protect the opener from here.
        window.open(`/present/templates/${templateId}/${dayNumber}`, '_blank');
    }, [hasUnsavedChanges, templateId]);

    const handleDuplicateDay = useCallback((dayNumber) => {
        if (numberOfDays >= 14 || !template) return;

        const workout = template.workouts?.find(w => w.index === dayNumber);
        if (!workout) return;

        const newIndex = numberOfDays;
        const duplicated = duplicateWorkoutDay(workout, newIndex);

        setTemplateWithDirty(prev => ({
            ...prev,
            workouts: [...(prev.workouts || []), duplicated]
        }));
        setNumberOfDays(prev => prev + 1);
    }, [numberOfDays, template, setTemplateWithDirty]);

    const handleWorkoutNameUpdate = useCallback((dayNumber, { name, nameHeb }) => {
        setTemplateWithDirty(prev => {
            if (!prev) return prev;
            const workoutIndex = prev.workouts?.findIndex(workout => workout.index === dayNumber);
            if (workoutIndex === -1) return prev;

            const updatedTemplate = { ...prev };
            updatedTemplate.workouts = [...prev.workouts];
            updatedTemplate.workouts[workoutIndex] = { ...prev.workouts[workoutIndex], name, nameHeb };
            return updatedTemplate;
        });
    }, [setTemplateWithDirty]);

    // Delete day
    const deleteDay = useCallback((dayToDelete) => {
        if (computeReadOnly(template)) {
            setError(readOnlyMessage);
            return;
        }
        if (numberOfDays > 1 && template) {
            const updatedWorkouts = template.workouts
                ?.filter(workout => workout.index !== dayToDelete)
                .map((workout, index) => ({
                    ...workout,
                    index: index,
                    name: workout.name || `Workout ${index + 1}`,
                    nameHeb: workout.nameHeb || `אימון ${index + 1}`
                })) || [];

            setTemplateWithDirty({ ...template, workouts: updatedWorkouts });
            setNumberOfDays(prev => prev - 1);
        }
    }, [template, numberOfDays, computeReadOnly, readOnlyMessage, setTemplateWithDirty]);

    const exercisesByDay = useMemo(() => {
        if (!template?.workouts) return {};
        return template.workouts.reduce((acc, workout) => {
            acc[workout.index] = workout.exercises || [];
            return acc;
        }, {});
    }, [template?.workouts]);

    if (templateLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", backgroundColor: '#fafafa' }}>
                <Typography variant="h6">טוען תבנית...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", backgroundColor: '#fafafa' }}>
            {/* Header */}
            <Container maxWidth={false} sx={{ px: 3, pt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <IconButton
                        onClick={() => navigate('/app/templates')}
                        sx={{ color: "#64748B", "&:hover": { backgroundColor: "#F1F5F9", color: "#1E293B" } }}
                    >
                        <ArrowForward />
                    </IconButton>
                    <Typography sx={{ fontSize: "24px", fontWeight: 700, color: "#1E293B" }}>
                        עריכת תבנית
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {template && (
                        <RTL>
                            <Button
                                variant="contained"
                                startIcon={<SparkleIcon />}
                                onClick={() => setAiDrawerOpen(true)}
                                sx={{
                                    background: 'linear-gradient(135deg, #01BBA7 0%, #019385 100%)',
                                    color: 'white',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                    px: 2,
                                    boxShadow: '0 2px 8px rgba(1, 187, 167, 0.3)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #019385 0%, #017d71 100%)',
                                    },
                                }}
                            >
                                בנה עם AI
                            </Button>
                        </RTL>
                    )}
                </Box>
            </Container>

            {/* Template Metadata */}
            <Container maxWidth={false} sx={{ px: 3 }}>
                <CollapsibleTemplateOverview
                    template={template}
                    hasUnsavedChanges={hasUnsavedChanges}
                    saving={saving}
                    handleSaveTemplate={handleSaveTemplate}
                    onTemplateUpdate={handleTemplateUpdate}
                    onAssignCompanies={handleAssignCompanies}
                />
            </Container>

            {/* Workout Days - Full Width (no sidebar) */}
            <WorkoutDaysSection
                onWorkoutNameUpdate={handleWorkoutNameUpdate}
                workouts={template?.workouts || []}
                numberOfDays={numberOfDays}
                exercisesByDay={exercisesByDay}
                isSuperAdmin={isSuperAdmin}
                isSystemResource={template?.systemResource}
                readOnly={isReadOnly}
                onDeleteDay={deleteDay}
                onAddNewDay={addNewDay}
                onRemoveExerciseFromDay={removeExerciseFromDay}
                onExerciseUpdate={handleExerciseUpdate}
                onReorderExercises={handleReorderExercises}
                onAddExercises={handleAddExercises}
                onAddSuperSet={handleAddSuperSet}
                onDuplicateDay={handleDuplicateDay}
                onPresentDay={handlePresentDay}
            />

            {/* Success/Error Messages */}
            <Snackbar open={saveSuccess} autoHideDuration={3000} onClose={() => setSaveSuccess(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
                    התבנית נשמרה בהצלחה!
                </Alert>
            </Snackbar>
            <Snackbar open={!!saveError} autoHideDuration={5000} onClose={() => setSaveError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSaveError(null)} severity="error" sx={{ width: '100%' }}>
                    {saveError}
                </Alert>
            </Snackbar>
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            {/* AI Workout Drawer */}
            <WorkoutAiDrawer
                open={aiDrawerOpen}
                onClose={() => setAiDrawerOpen(false)}
                template={template}
                programContext={aiProgramContext}
                onApply={handleAiApply}
            />
        </Box>
    );
};

export default NewTemplatePage;
